import { Component, Inject, OnDestroy, OnInit } from "@angular/core";
import { UntypedFormArray, UntypedFormBuilder, UntypedFormControl, UntypedFormGroup, Validators } from "@angular/forms";
import { MatDialogRef, MAT_DIALOG_DATA } from "@angular/material/dialog";
import { MatSnackBar } from "@angular/material/snack-bar";
import { Router } from "@angular/router";
import { FileLogger } from "@comunicare/comunicare-helper";
import { ICareplanInfo, ITranslation } from "@comunicare/comunicare-interfaces";
import { TranslateService } from "@ngx-translate/core";
import { forkJoin, of, Subject } from "rxjs";
import { takeUntil } from "rxjs/operators";
import { CareplanEditorService } from "src/app/careplan-editor/domain/careplan-editor.service";
import { Healthcareservice } from "src/app/models/healthcareservice.model";
import { LanguagesApiService } from "src/app/providers/api/languages-api.service";
import { HealthcareserviceService } from "src/app/providers/healthcareservice.service";
import { LanguagesService } from "src/app/providers/languages.service";
import { UserService } from "src/app/providers/user.service";

@Component({
  selector: "app-duplicate-careplan-modal",
  templateUrl: "./duplicate-careplan-modal.component.html",
  styleUrls: ["./duplicate-careplan-modal.component.scss"],
})
export class DuplicateCareplanModalComponent implements OnInit, OnDestroy {
  private onDestroy$ = new Subject<void>();
  public isLoading = true;
  public form = this.fb.group({
    description: this.fb.group({
      isTranslated: false,
      defaultLang: "fr",
      translations: this.fb.array([
        this.fb.group({
          translateKey: this.fb.control("", Validators.required),
          lang: this.fb.control("fr"),
        }),
      ]),
    }),
    // At least one service is mandatory to save the careplan template
    services: this.fb.array([], Validators.required),
  });
  public availableServices: {
    checked: boolean;
    service: Healthcareservice;
  }[];
  public availableLangs: ITranslation[] = [];

  constructor(
    private careplanEditorService: CareplanEditorService,
    @Inject(MAT_DIALOG_DATA)
    public data: { careplanInfo: ICareplanInfo },
    private fb: UntypedFormBuilder,
    private router: Router,
    private healthcareService: HealthcareserviceService,
    private languagesService: LanguagesService,
    private languageApiService: LanguagesApiService,
    private dialogRef: MatDialogRef<DuplicateCareplanModalComponent>,
    private userService: UserService,
    private snackBar: MatSnackBar,
    private translateService: TranslateService
  ) {}
  ngOnInit(): void {
    this.healthcareService
      .watchAvailableServices()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((services) => {
        this.availableServices = services.map((s) => {
          return {
            checked: false,
            service: s,
          };
        });
      });
    this.languagesService
      .list()
      .pipe(takeUntil(this.onDestroy$))
      .subscribe((languages) => {
        this.availableLangs = languages;
        this.isLoading = false;
      });
  }

  ngOnDestroy(): void {
    this.onDestroy$.next();
    this.onDestroy$.complete();
  }

  public confirm(): void {
    if (!this.form.valid) {
      // Display the errors of the invalid fields
      this.form.markAllAsTouched();
      return;
    }
    const formValues = this.form.getRawValue();
    // Without this right, the duplicated careplan would get a name that is not translated anywhere
    if (formValues.description.isTranslated && !this.userService.isAuthorizedSync(null, "dashboard/translations", "POST")) {
      FileLogger.warn("DuplicateCareplanModalComponent", "User does not have access to: POST dashboard/translations");
      this.snackBar.open(this.translateService.instant("api.errors.ACCESS DENIED"), "ok", { duration: 5000 });
      return;
    }
    this.isLoading = true;

    const translation$ = formValues.description.isTranslated
      ? (() => {
          const newTranslation = this.careplanEditorService.formatTranslationFormToItranslation(
            formValues.description.translations,
            formValues.description.defaultLang,
            this.availableLangs.map((l) => l.term)
          );
          if (newTranslation) {
            formValues.description.translations[0].translateKey = "$TR$" + newTranslation.term;
            return this.languageApiService.createTranslations([newTranslation]);
          }
          return of(null);
        })()
      : of(null);

    forkJoin({
      translation: translation$,
      careplan: this.careplanEditorService.duplicateCareplanTemplate(
        this.data.careplanInfo.support,
        formValues.description.translations[0].translateKey,
        formValues.services
      ),
    })
      .pipe(takeUntil(this.onDestroy$))
      .subscribe(({ careplan }) => {
        this.dialogRef.close(true);
        this.router.navigate(["/careplanEditor", careplan.support[0].reference, "general", "visualView"]);
      });
  }

  /**
   * Return the services field form
   */
  public get serviceForm(): UntypedFormArray {
    return this.form.get("services") as UntypedFormArray;
  }

  /**
   * Return the description field form
   */
  public get descriptionForm(): UntypedFormGroup {
    return this.form.get("description") as UntypedFormGroup;
  }

  /**
   * Return the description.translations field form
   */
  public get descriptionTradForm(): UntypedFormArray {
    return this.descriptionForm.get("translations") as UntypedFormArray;
  }

  /**
   * update the form tu set/unset the service to the careplan form
   * @param service - the service that the user clicked on
   */
  public healthcareServiceChange(service: { checked: boolean; service: Healthcareservice }): void {
    service.checked = !service.checked;
    // Display the error as soon as the user unselects the last service
    this.serviceForm.markAsTouched();
    const s = service.service;
    if (service.checked) {
      // add service to form
      this.serviceForm.push(
        new UntypedFormGroup({
          reference: new UntypedFormControl(s.identifier[0].value),
          display: new UntypedFormControl(s.identifier[0].label),
        })
      );
    } else {
      // delete service to form
      const index = this.serviceForm.value.findIndex((sf) => sf.reference == s.identifier[0].value);
      if (index !== -1) {
        this.serviceForm.removeAt(index);
      } else {
        FileLogger.warn("healthcareServiceChange", "Service not found " + s.identifier[0].value);
      }
    }
  }
}
